<template>
  <div class="aiffect-chain">
    <!-- Header with actions -->
    <div class="chain-header">
      <h2>AIffect Chains</h2>
      <el-button type="primary" @click="showCreateDialog">
        <i class="el-icon-plus"></i> Create Chain
      </el-button>
    </div>

    <!-- Chain List -->
    <div class="chain-list">
      <div 
        v-for="chain in chains" 
        :key="(chain._id.$oid || chain._id.$id)" 
        class="chain-card"
      >
        <div class="card-header">
          <div class="chain-name">
            <i class="el-icon-connection"></i>
            {{ chain.name || 'Untitled Chain' }}
            <el-tag 
              v-if="chain.store_public === 'YES'" 
              type="success" 
              size="mini"
              class="public-badge"
            >
              <i class="el-icon-view"></i> Public
            </el-tag>
          </div>
          
          <div class="chain-meta">
            <span class="effect-count">
              <i class="el-icon-link"></i>
              {{ getEffectCount(chain) }} effects
            </span>
            <span class="execution-count">
              <i class="el-icon-video-camera"></i>
              {{ getRecordCount(chain) }} executions
            </span>
          </div>
          
          <div class="chain-actions">
            <el-button 
              size="mini" 
              type="primary" 
              plain
              @click="viewChain(chain)"
              class="action-btn view-btn"
            >
              <i class="el-icon-view"></i>
            </el-button>
            <el-button 
              size="mini" 
              type="warning" 
              plain
              @click="editChain(chain)"
              class="action-btn edit-btn"
            >
              <i class="el-icon-edit"></i>
            </el-button>
            <el-button 
              size="mini" 
              type="success" 
              @click="executeChain(chain)"
              class="action-btn execute-btn"
            >
              <i class="el-icon-video-play"></i>
            </el-button>
            <el-button 
              size="mini" 
              type="info" 
              plain
              @click="shareChain(chain)"
              class="action-btn share-btn"
            >
              <i class="el-icon-share"></i>
            </el-button>
            <el-button 
              size="mini" 
              type="danger" 
              plain
              @click="deleteChain(chain)"
              class="action-btn delete-btn"
            >
              <i class="el-icon-delete"></i>
            </el-button>
          </div>
        </div>
        
        <div class="card-content">
          <div class="chain-description">
            {{ chain.description || 'No description available' }}
          </div>
          
          <div class="chain-footer">
            <div class="chain-collaborators" v-if="chain.collaborator_ids && chain.collaborator_ids.length > 0">
              <el-tooltip content="Shared with collaborators" placement="top">
                <span class="collaborator-indicator">
                  <i class="el-icon-user"></i>
                  {{ chain.collaborator_ids.length }}
                </span>
              </el-tooltip>
            </div>
            <div class="chain-status">
              <el-tag 
                :type="getStatusType(chain)" 
                size="mini"
                class="status-tag"
              >
                {{ getStatusText(chain) }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-if="chains.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="el-icon-connection"></i>
        </div>
        <h3>No chains created yet</h3>
        <p>Start building your AI workflows by creating your first chain</p>
        <el-button type="primary" size="medium" @click="showCreateDialog">
          <i class="el-icon-plus"></i>
          Create Your First Chain
        </el-button>
      </div>
    </div>

    <!-- Create/Edit Dialog -->
    <el-dialog
      :title="dialogMode === 'create' ? 'Create Aiffect Chain' : 'Edit Aiffect Chain'"
      :visible.sync="dialogVisible"
      width="80%"
      :close-on-click-modal="false"
      @close="handleDialogClose"
      custom-class="chain-edit-dialog"
    >
      <div class="chain-edit-container">
        <!-- Chain Name Section -->
        <div class="form-section">
          <div class="section-header">
            <i class="el-icon-edit"></i>
            <h3>Chain Information</h3>
          </div>
          <el-form :model="chainForm" :rules="chainRules" ref="chainForm" label-width="0">
            <el-form-item prop="name" class="chain-name-item">
              <div class="name-input-container">
                <el-input
                  v-model="chainForm.name"
                  placeholder="Enter a descriptive name for your AI chain (optional if description is provided)"
                  maxlength="100"
                  show-word-limit
                  size="large"
                  class="chain-name-input"
                >
                  <template slot="prepend">
                    <i class="el-icon-connection"></i>
                  </template>
                </el-input>
                <el-button 
                  type="text" 
                  size="mini" 
                  @click="generateAITitle"
                  :loading="generatingTitle"
                  :disabled="!chainForm.description && (!chainForm.effect_list || chainForm.effect_list.length === 0)"
                  class="ai-generate-title-btn"
                >
                  <i class="el-icon-magic-stick"></i>
                  Generate by AI
                </el-button>
              </div>
            </el-form-item>
            
            <!-- Chain Description -->
            <el-form-item class="chain-description-item">
              <div class="description-container">
                <div class="description-header">
                  <label class="description-label">
                    <i class="el-icon-document"></i>
                    Description
                  </label>
                  <el-button 
                    type="text" 
                    size="mini" 
                    @click="generateAIDescription"
                    :loading="generatingDescription"
                    class="ai-generate-btn"
                  >
                    <i class="el-icon-magic-stick"></i>
                    Generate by AI
                  </el-button>
                </div>
                <el-input
                  type="textarea"
                  v-model="chainForm.description"
                  placeholder="Describe what this workflow accomplishes and its purpose..."
                  :autosize="{ minRows: 3, maxRows: 6 }"
                  maxlength="500"
                  show-word-limit
                  class="chain-description-input"
                ></el-input>
              </div>
            </el-form-item>

            <!-- System Prompt -->
            <el-form-item class="system-prompt-item">
              <div class="system-prompt-container">
                <div class="system-prompt-header">
                  <label class="system-prompt-label">
                    <i class="el-icon-s-tools"></i>
                    System Prompt
                  </label>
                  <el-tooltip 
                    content="This system prompt will be prepended to the AI model's context for all effects in this chain" 
                    placement="top"
                  >
                    <i class="el-icon-question info-icon"></i>
                  </el-tooltip>
                </div>
                <el-input
                  type="textarea"
                  v-model="chainForm.system_prompt"
                  placeholder="Enter a system prompt that will be applied to all AI effects in this chain (optional). For example: 'You are a professional content writer...' or 'Always respond in a structured format...'"
                  :autosize="{ minRows: 3, maxRows: 6 }"
                  class="system-prompt-input"
                ></el-input>
                <div class="system-prompt-description">
                  This prompt will set the behavior and context for the AI across all steps in this workflow
                </div>
              </div>
            </el-form-item>

            <!-- Store Public Option -->
            <el-form-item class="store-public-item">
              <div class="store-public-container">
                <span class="store-public-label">
                  <i class="el-icon-view"></i>
                  Make this workflow public:
                </span>
                <el-radio-group v-model="chainForm.store_public" size="small">
                  <el-radio label="NO">NO (Private)</el-radio>
                  <el-radio label="YES">YES (Public)</el-radio>
                </el-radio-group>
                <el-tooltip 
                  content="Public workflows can be discovered and used by other users" 
                  placement="top"
                >
                  <i class="el-icon-question info-icon"></i>
                </el-tooltip>
              </div>
            </el-form-item>
          </el-form>
        </div>
        
        <!-- Effects Section -->
        <div class="form-section">
          <div class="section-header">
            <i class="el-icon-magic-stick"></i>
            <h3>AI Effects ({{ chainForm.effect_list.length }})</h3>
            <div class="header-buttons">
              <el-button 
                type="text" 
                size="small" 
                @click="generateAIEffects"
                :loading="generatingEffects"
                :disabled="!canGenerateEffects"
                icon="el-icon-magic-stick"
                class="ai-generate-effects-btn"
              >
                Generate by AI
              </el-button>
              <el-button 
                type="primary" 
                size="small" 
                @click="addEffect"
                icon="el-icon-plus"
                class="add-effect-header-btn"
              >
                Add Effect
              </el-button>
            </div>
          </div>
          
          <div class="effects-container">
            <div
              v-for="(effect, index) in chainForm.effect_list"
              :key="index"
              class="effect-card"
              :class="{ 'effect-disabled': effect.enabled === false }"
            >
              <div class="effect-card-header">
                <div class="effect-number-badge">
                  <span>{{ index + 1 }}</span>
                </div>
                <div class="effect-title">
                  <el-input
                    v-model="chainForm.effect_list[index].name"
                    placeholder="Effect name"
                    maxlength="100"
                    size="small"
                    class="effect-name-input"
                  ></el-input>
                </div>
                <div class="effect-actions">
                  <el-switch
                    v-model="chainForm.effect_list[index].enabled"
                    active-text="Enabled"
                    inactive-text="Disabled"
                    size="small"
                    class="effect-enabled-switch"
                  ></el-switch>
                  <el-button
                    size="mini"
                    type="text"
                    @click="moveEffectUp(index)"
                    :disabled="index === 0"
                    class="move-btn"
                  >
                    <i class="el-icon-top"></i>
                  </el-button>
                  <el-button
                    size="mini"
                    type="text"
                    @click="moveEffectDown(index)"
                    :disabled="index === chainForm.effect_list.length - 1"
                    class="move-btn"
                  >
                    <i class="el-icon-bottom"></i>
                  </el-button>
                  <el-button
                    size="mini"
                    type="text"
                    style="color: #f56c6c;"
                    @click="removeEffect(index)"
                    :disabled="chainForm.effect_list.length <= 1"
                    class="delete-btn"
                  >
                    <i class="el-icon-delete"></i>
                  </el-button>
                </div>
              </div>
              
                             <div class="effect-card-content">
                 <!-- Model and Processing Options - Side by Side -->
                 <div class="effect-form-row">
                   <div class="effect-form-field model-field">
                     <label class="field-label">
                       <i class="el-icon-cpu"></i>
                       AI Model
                     </label>
                     <el-select
                       v-model="chainForm.effect_list[index].model"
                       placeholder="Select AI model"
                       class="effect-model-select"
                       filterable
                       size="small"
                     >
                       <el-option-group label="OpenAI">
                         <el-option value="gpt-5" label="GPT-5"></el-option>
                         <el-option value="gpt-4.1" label="GPT-4.1"></el-option>
                         <el-option value="gpt-4o" label="GPT-4o"></el-option>
                         <el-option value="gpt-4" label="GPT-4"></el-option>
                         <el-option value="gpt-3.5-turbo" label="GPT-3.5 Turbo"></el-option>
                       </el-option-group>
                       <el-option-group label="Claude">
                         <el-option value="claude-3-5-sonnet-20240620" label="Claude 3.5 Sonnet"></el-option>
                         <el-option value="claude-3-opus-20240229" label="Claude 3 Opus"></el-option>
                         <el-option value="claude-3-sonnet-20240229" label="Claude 3 Sonnet"></el-option>
                         <el-option value="claude-3-haiku-20240307" label="Claude 3 Haiku"></el-option>
                       </el-option-group>
                       <el-option-group label="DeepSeek">
                         <el-option value="deepseek-chat" label="DeepSeek Chat"></el-option>
                         <el-option value="deepseek-reasoner" label="DeepSeek Reasoner"></el-option>
                         <el-option value="siliconflow/Pro/deepseek-ai/DeepSeek-R1" label="DeepSeek R1"></el-option>
                         <el-option value="siliconflow/Pro/deepseek-ai/DeepSeek-V3" label="DeepSeek V3"></el-option>
                       </el-option-group>
                       <el-option-group label="Google">
                         <el-option value="gemini-3-pro-image-preview" label="Gemini 3 Pro Image Preview"></el-option>
                       </el-option-group>
                       <el-option-group label="Other">
                         <el-option value="midjourney" label="Midjourney"></el-option>
                         <el-option value="llama3" label="Llama 3"></el-option>
                         <el-option value="moonshot-v1-8k" label="Moonshot v1-8k"></el-option>
                         <el-option value="moonshot-v1-32k" label="Moonshot v1-32k"></el-option>
                         <el-option value="moonshot-v1-128k" label="Moonshot v1-128k"></el-option>
                         <el-option value="o1-preview" label="O1 Preview"></el-option>
                         <el-option value="o1-mini" label="O1 Mini"></el-option>
                       </el-option-group>
                     </el-select>
                   </div>
                   
                   <div class="effect-form-field processing-field">
                     <label class="field-label">
                       <i class="el-icon-setting"></i>
                       Processing
                     </label>
                     <div class="processing-options">
                       <el-switch 
                         v-model="chainForm.effect_list[index].is_splice" 
                         active-text="Split"
                         inactive-text="Standard"
                         class="splice-switch"
                         size="small"
                       ></el-switch>
                       <div class="switch-description">
                         Split large content into chunks
                       </div>
                     </div>
                   </div>
                 </div>
                 
                 <!-- Output Form Selection - Full Width -->
                 <div class="effect-form-field output-form-field">
                   <label class="field-label">
                     <i class="el-icon-folder"></i>
                     Output Form
                   </label>
                   <el-select
                     v-model="chainForm.effect_list[index].output_form"
                     placeholder="Select a Data Object to standardize output (optional)"
                     class="effect-output-form-select"
                     clearable
                     filterable
                     size="small"
                     :loading="!dataObjects || dataObjects.length === 0"
                   >
                     <el-option
                       v-for="(dataObject, objIndex) in dataObjects"
                       :key="dataObject._id?.$oid || dataObject._id?.$id || dataObject._id || dataObject.id || objIndex"
                       :label="dataObject.name"
                       :value="dataObject._id?.$oid || dataObject._id?.$id || dataObject._id || dataObject.id"
                     >
                       <span style="float: left">{{ dataObject.name }}</span>
                       <span style="float: right; color: #8492a6; font-size: 13px">
                         <el-tag :type="dataObject.type === 'ARRAY' ? 'warning' : 'primary'" size="mini">
                           {{ dataObject.type }}
                         </el-tag>
                       </span>
                     </el-option>
                     <div v-if="dataObjects.length === 0" slot="empty" style="padding: 10px 0; text-align: center; color: #909399;">
                       No Data Objects available. Create one in Data Object Management.
                     </div>
                   </el-select>
                   <div class="output-form-description">
                     Select a Data Object to standardize and structure the output of this AI effect
                   </div>
                 </div>
                 
                 <!-- Prompt/Description Field - Full Width Below -->
                 <div class="effect-form-field prompt-field">
                   <label class="field-label">
                     <i class="el-icon-chat-dot-round"></i>
                     Prompt/Description
                   </label>
                   <el-input
                     type="textarea"
                     v-model="chainForm.effect_list[index].prop"
                     placeholder="Describe what this effect should do in detail. Include context, instructions, and any specific requirements for the AI model..."
                     :autosize="{ minRows: 4, maxRows: 8 }"
                     maxlength="2000"
                     show-word-limit
                     class="effect-prompt-input"
                   ></el-input>
                 </div>
               </div>
            </div>
            
            <!-- Empty effects state -->
            <div v-if="chainForm.effect_list.length === 0" class="empty-effects">
              <div class="empty-effects-icon">
                <i class="el-icon-magic-stick"></i>
              </div>
              <h4>No effects added yet</h4>
              <p>Add your first AI effect to start building your chain</p>
              <el-button 
                type="primary" 
                @click="addEffect"
                icon="el-icon-plus"
              >
                Add First Effect
              </el-button>
            </div>
          </div>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" size="medium">Cancel</el-button>
        <el-button 
          type="primary" 
          @click="saveChain" 
          :loading="saving"
          size="medium"
          class="save-btn"
        >
          <i class="el-icon-check"></i>
          {{ dialogMode === 'create' ? 'Create Chain' : 'Update Chain' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- View Chain Dialog -->
    <el-dialog
      title="Chain Details"
      :visible.sync="viewDialogVisible"
      width="80%"
      :close-on-click-modal="false"
    >
      <div v-if="selectedChain" class="chain-details">
        <el-descriptions title="Chain Information" :column="2" border>
          <el-descriptions-item label="Name">{{ selectedChain.name || 'Untitled Chain' }}</el-descriptions-item>
          <el-descriptions-item label="Effects">{{ getEffectCount(selectedChain) }}</el-descriptions-item>
          <el-descriptions-item label="Executions">{{ getRecordCount(selectedChain) }}</el-descriptions-item>
          <el-descriptions-item label="ID">{{ selectedChain._id }}</el-descriptions-item>
        </el-descriptions>
        
        <div class="effects-section">
          <h3>Effects</h3>
          <el-timeline>
            <el-timeline-item
              v-for="(effect, index) in selectedChain.effect_list || []"
              :key="index"
              :timestamp="`Effect ${index + 1}: ${getEffectDisplayName(effect)}`"
              placement="top"
            >
              <div class="effect-content">
                <div v-if="typeof effect === 'object' && effect.name" class="effect-details">
                  <div class="effect-name"><strong>Name:</strong> {{ effect.name }}</div>
                  <div class="effect-prop"><strong>Prompt:</strong> {{ effect.prop }}</div>
                  <div v-if="effect.model" class="effect-model"><strong>Model:</strong> {{ effect.model }}</div>
                  <div v-if="effect.output_form" class="effect-output-form">
                    <strong>Output Form:</strong> 
                    <el-tag type="info" size="small">{{ getDataObjectName(effect.output_form) }}</el-tag>
                  </div>
                </div>
                <div v-else class="effect-legacy">{{ effect }}</div>
              </div>
            </el-timeline-item>
          </el-timeline>
        </div>
        
        <div v-if="selectedChain.chain_records && selectedChain.chain_records.length > 0" class="records-section">
          <h3>Execution Records</h3>
          <el-table :data="selectedChain.chain_records" stripe>
            <el-table-column prop="name" label="Name" width="200"></el-table-column>
            <el-table-column label="Created" width="180">
              <template slot-scope="scope">
                {{ formatTime(scope.row.create_time) }}
              </template>
            </el-table-column>
            <el-table-column label="Results">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.exe_result && scope.row.exe_result.length > 0" type="success">
                  {{ scope.row.exe_result.length }} results
                </el-tag>
                <el-tag v-else type="info">No results</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Actions" width="120">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="viewRecord(scope.row)">
                  <i class="el-icon-view"></i> View
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-dialog>

    <!-- Share Chain Dialog -->
    <el-dialog
      title="Share Workflow Chain"
      :visible.sync="shareDialogVisible"
      width="60%"
      :close-on-click-modal="false"
      custom-class="share-dialog"
    >
      <div v-if="sharingChain" class="share-container">
        <div class="share-header">
          <h3>{{ sharingChain.name || 'Untitled Chain' }}</h3>
          <p class="share-description">Add collaborators who can view and execute this workflow chain</p>
        </div>
        
        <div class="collaborator-section">
          <div class="add-collaborator">
            <el-input
              v-model="newCollaboratorEmail"
              placeholder="Enter user email to add as collaborator"
              @keyup.enter="addCollaborator"
              size="medium"
            >
              <el-button 
                slot="append" 
                icon="el-icon-plus"
                @click="addCollaborator"
                :disabled="!newCollaboratorEmail"
              >
                Add
              </el-button>
            </el-input>
          </div>
          
          <div class="current-collaborators" v-if="currentCollaborators.length > 0">
            <h4>Current Collaborators ({{ currentCollaborators.length }})</h4>
            <div class="collaborator-list">
              <div 
                v-for="(collaborator, index) in currentCollaborators" 
                :key="index"
                class="collaborator-item"
              >
                <div class="collaborator-info">
                  <i class="el-icon-user-solid"></i>
                  <span>{{ collaborator }}</span>
                </div>
                <el-button
                  type="text"
                  size="small"
                  @click="removeCollaborator(index)"
                  class="remove-btn"
                >
                  <i class="el-icon-close"></i>
                </el-button>
              </div>
            </div>
          </div>
          
          <div class="no-collaborators" v-else>
            <i class="el-icon-user"></i>
            <p>No collaborators added yet</p>
          </div>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="shareDialogVisible = false" size="medium">Cancel</el-button>
        <el-button 
          type="primary" 
          @click="saveCollaborators" 
          :loading="savingCollaborators"
          size="medium"
        >
          <i class="el-icon-check"></i>
          Save Collaborators
        </el-button>
      </span>
    </el-dialog>

    <!-- Execute Chain Dialog -->
    <el-dialog
      title="Execute Chain"
      :visible.sync="executeDialogVisible"
      width="60%"
      :close-on-click-modal="false"
    >
      <div v-if="executingChain">
        <p>Execute chain: <strong>{{ executingChain.name }}</strong></p>
        <el-form :model="executeForm" ref="executeForm" label-width="120px">
          <el-form-item label="Execution Name">
            <el-input v-model="executeForm.name" placeholder="Enter execution name"></el-input>
          </el-form-item>
          <el-form-item label="Parameters">
            <el-input
              type="textarea"
              v-model="executeForm.parameters"
              placeholder="Enter execution parameters (JSON format)"
              :autosize="{ minRows: 3, maxRows: 6 }"
            ></el-input>
          </el-form-item>
        </el-form>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="executeDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="runChainExecution" :loading="executing">
          Execute
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { getUserId } from '@/utils/store'  

export default {
  name: 'AiffectChain',
  data() {
    return {
      chains: [],
      loading: false,
      dialogVisible: false,
      viewDialogVisible: false,
      executeDialogVisible: false,
      shareDialogVisible: false,
      dialogMode: 'create',
      saving: false,
      executing: false,
      generatingTitle: false,
      generatingDescription: false,
      generatingEffects: false,
      selectedChain: null,
      executingChain: null,
      sharingChain: null,
      currentCollaborators: [],
      newCollaboratorEmail: '',
      savingCollaborators: false,
      dataObjects: [], // Store available data objects
      chainForm: {
        id: '',
        name: '',
        description: '',
        system_prompt: '',
        store_public: 'NO',
        effect_list: [{ name: '', prop: '', model: '', is_splice: false, output_form: '', enabled: true }]
      },
      executeForm: {
        name: '',
        parameters: ''
      },
      chainRules: {
        name: [
          { 
            validator: (rule, value, callback) => {
              // Name is required only if description is empty and no effects are configured
              if (!value && !this.chainForm.description && (!this.chainForm.effect_list || this.chainForm.effect_list.length === 0 || this.chainForm.effect_list.every(effect => !effect.name && !effect.prop))) {
                callback(new Error('Chain name is required when description and effects are not provided'))
              } else {
                callback()
              }
            }, 
            trigger: 'blur' 
          },
          { min: 0, max: 100, message: 'Length should be 0 to 100 characters', trigger: 'blur' }
        ],
        effect_list: [
          { 
            required: true, 
            validator: (rule, value, callback) => {
              if (!value || value.length === 0) {
                callback(new Error('At least one effect is required'))
              } else {
                // Allow saving even if some effects are incomplete
                // Just ensure we have at least one effect
                callback()
              }
            }, 
            trigger: 'change' 
          }
        ]
      }
    }
  },
  computed: {
    canGenerateEffects() {
      // Can only generate effects if effects are currently blank and we have name or description
      const hasBlankEffects = !this.chainForm.effect_list || 
        this.chainForm.effect_list.length === 0 || 
        this.chainForm.effect_list.every(effect => !effect.name && !effect.prop)
      const hasNameOrDescription = this.chainForm.name || this.chainForm.description
      return hasBlankEffects && hasNameOrDescription
    }
  },
  mounted() {
    this.loadChains()
    this.loadDataObjects()
  },
  methods: {
    async loadDataObjects() {
      try {
        const response = await postRequest('getMyDataObjects', {
          user_id: getUserId()
        })
        console.log('Data objects response:', response.data)
        if (response.data && response.data.data) {
          this.dataObjects = response.data.data || []
          console.log('Loaded data objects:', this.dataObjects)
        } else {
          this.dataObjects = []
          console.warn('Failed to load data objects:', response.data)
        }
      } catch (error) {
        console.error('Error loading data objects:', error)
        this.dataObjects = []
      }
    },
    async loadChains() {
      try {
        this.loading = true
        const response = await postRequest('getChainList', { 
          user_id: getUserId(),
          include_shared: true 
        })
        if (response.data && response.data.code === 0) {
          this.chains = response.data.data || []
        } else {
          // Fallback to original API if new one doesn't exist
          const fallbackResponse = await postRequest('getChainList', { user_id: getUserId() })
          if (fallbackResponse.data && fallbackResponse.data.code === 0) {
            this.chains = fallbackResponse.data.data || []
          } else {
            this.$message.error('Failed to load chains: ' + (fallbackResponse.data && fallbackResponse.data.msg ? fallbackResponse.data.msg : 'Unknown error'))
          }
        }
      } catch (error) {
        console.error('Error loading chains:', error)
        this.$message.error('Failed to load chains')
      } finally {
        this.loading = false
      }
    },

    async viewChain(chain) {
      try {
        const response = await postRequest('getChain', { id: (chain._id.$oid || chain._id.$id) || chain._id, user_id: getUserId() })
        if (response.data && response.data.code === 0) {
          this.selectedChain = response.data.data
          this.viewDialogVisible = true
        } else {
          this.$message.error('Failed to load chain details: ' + (response.data && response.data.msg ? response.data.msg : 'Unknown error'))
        }
      } catch (error) {
        console.error('Error loading chain details:', error)
        this.$message.error('Failed to load chain details')
      }
    },

    async showCreateDialog() {
      this.dialogMode = 'create'
      this.chainForm = {
        id: '',
        name: '',
        description: '',
        system_prompt: '',
        store_public: 'NO',
        effect_list: [{ name: '', prop: '', model: '', is_splice: false, output_form: '', enabled: true }]
      }
      await this.loadDataObjects() // Reload data objects when dialog opens
      this.dialogVisible = true
    },

    async editChain(chain) {
      this.dialogMode = 'edit'
      this.chainForm = {
        id: (chain._id.$oid || chain._id.$id),
        name: chain.name || '',
        description: chain.description || '',
        system_prompt: chain.system_prompt || '',
        store_public: chain.store_public || 'NO',
        effect_list: chain.effect_list && chain.effect_list.length > 0 ?
          chain.effect_list.map(effect =>
            typeof effect === 'string' ?
              { name: '', prop: effect, model: '', is_splice: false, output_form: '', enabled: true } :
              {
                name: effect.name || '',
                prop: effect.prop || '',
                model: effect.model || '',
                is_splice: effect.is_splice || false,
                output_form: effect.output_form || '',
                enabled: effect.enabled !== undefined ? effect.enabled : true
              }
          ) : [{ name: '', prop: '', model: '', is_splice: false, output_form: '', enabled: true }]
      }
      await this.loadDataObjects() // Reload data objects when dialog opens
      this.dialogVisible = true
    },

    shareChain(chain) {
      this.sharingChain = chain
      this.currentCollaborators = chain.collaborator_ids || []
      this.newCollaboratorEmail = ''
      this.shareDialogVisible = true
    },

    addCollaborator() {
      if (!this.newCollaboratorEmail) {
        this.$message.warning('Please enter a user email')
        return
      }
      
      const email = this.newCollaboratorEmail.trim()
      
      // Check if already added
      if (this.currentCollaborators.includes(email)) {
        this.$message.warning('This user is already a collaborator')
        return
      }
      
      // Add to list
      this.currentCollaborators.push(email)
      this.newCollaboratorEmail = ''
      this.$message.success('Collaborator added to list')
    },

    removeCollaborator(index) {
      const removed = this.currentCollaborators.splice(index, 1)[0]
      this.$message.info(`Removed ${removed} from collaborators`)
    },

    async saveCollaborators() {
      try {
        this.savingCollaborators = true
        
        const response = await postRequest('updateChainCollaborators', {
          chain_id: (sharingChain._id.$oid || sharingChain._id.$id) || this.sharingChain._id,
          user_id: getUserId(),
          collaborator_ids: this.currentCollaborators
        })
        
        if (response.data && response.data.code === 0) {
          this.$message.success('Collaborators updated successfully')
          this.shareDialogVisible = false
          // Update the chain in the local list
          const chainIndex = this.chains.findIndex(c => 
            ((c._id.$oid || c._id.$id) || c._id) === ((sharingChain._id.$oid || sharingChain._id.$id) || this.sharingChain._id)
          )
          if (chainIndex !== -1) {
            this.$set(this.chains[chainIndex], 'collaborator_ids', this.currentCollaborators)
          }
          this.loadChains() // Reload to get updated data
        } else {
          // If the API doesn't exist yet, show a message
          this.$message.warning('Collaborator feature is being implemented. Please check back later.')
        }
      } catch (error) {
        console.error('Error saving collaborators:', error)
        this.$message.error('Failed to save collaborators')
      } finally {
        this.savingCollaborators = false
      }
    },

    executeChain(chain) {
      this.executingChain = chain
      this.executeForm = {
        name: `Execution ${new Date().toLocaleString()}`,
        parameters: ''
      }
      this.executeDialogVisible = true
    },

    async deleteChain(chain) {
      try {
        await this.$confirm(`Are you sure you want to delete "${chain.name || 'Untitled Chain'}"?`, 'Confirm Delete', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })
        
        const response = await postRequest('removeChain', { id: (chain._id.$oid || chain._id.$id), user_id: getUserId() })
        if (response.data && response.data.code === 0) {
          this.$message.success('Chain deleted successfully')
          this.loadChains()
        } else {
          this.$message.error('Failed to delete chain: ' + (response.data && response.data.msg ? response.data.msg : 'Unknown error'))
        }
      } catch (error) {
        if (error === 'cancel') {
          return
        }
        console.error('Error deleting chain:', error)
        this.$message.error('Failed to delete chain')
      }
    },

    async saveChain() {
      try {
        await this.$refs.chainForm.validate()
        
        this.saving = true
        
        // Prepare the effect list with proper structure
        const effectList = this.chainForm.effect_list
          .map(effect => ({
            name: effect.name || '',
            prop: effect.prop || '',
            model: effect.model || '',
            is_splice: effect.is_splice || false,
            output_form: effect.output_form || '',
            enabled: effect.enabled !== undefined ? effect.enabled : true
          }))
        
        console.log('Saving chain with effects:', effectList)
        console.log('Original effect list length:', this.chainForm.effect_list.length)
        console.log('Filtered effect list length:', effectList.length)
        
        // Auto-generate title if name is empty but description or effects exist
        let chainName = this.chainForm.name
        if (!chainName && (this.chainForm.description || effectList.some(e => e.name || e.prop))) {
          try {
            // Create a simple title generation prompt
            let prompt = 'Generate a concise title (max 50 characters) for this workflow based on:'
            
            if (this.chainForm.description) {
              prompt += `\nDescription: ${this.chainForm.description}`
            }
            
            if (effectList.length > 0) {
              const effectDescriptions = effectList
                .filter(e => e.name || e.prop)
                .slice(0, 3) // Use first 3 effects for title generation
                .map((e, i) => `${i + 1}. ${e.name || e.prop}`)
                .join('\n')
              if (effectDescriptions) {
                prompt += `\nSteps:\n${effectDescriptions}`
              }
            }
            
            prompt += '\n\nRespond with just the title, no quotes or additional text.'
            
            const response = await postRequest('exeModelGeneral', {
              content: prompt,
              model: 'gpt-4o-mini'
            })
            
            if (response.data && response.data.code === 0 && response.data.data) {
              chainName = response.data.data.trim().replace(/^"|"$/g, '').substring(0, 100)
              this.$message.info('Auto-generated title for the workflow')
            }
          } catch (error) {
            console.error('Error auto-generating title:', error)
            chainName = 'Untitled Workflow'
          }
        }
        
        const response = await postRequest('updateChain', {
          id: this.chainForm.id,
          name: chainName || 'Untitled Workflow',
          description: this.chainForm.description,
          system_prompt: this.chainForm.system_prompt,
          store_public: this.chainForm.store_public,
          user_id: getUserId(),
          effect_list: effectList
        })
        
        if (response.data && response.data.code === 0) {
          this.$message.success(this.dialogMode === 'create' ? 'Chain created successfully' : 'Chain updated successfully')
          this.dialogVisible = false
          this.loadChains()
        } else {
          this.$message.error('Failed to save chain: ' + (response.data && response.data.msg ? response.data.msg : 'Unknown error'))
        }
      } catch (error) {
        console.error('Error saving chain:', error)
        this.$message.error('Failed to save chain')
      } finally {
        this.saving = false
      }
    },

    async runChainExecution() {
      try {
        this.executing = true
        let exe_result = []
        
        if (this.executeForm.parameters) {
          try {
            exe_result = JSON.parse(this.executeForm.parameters)
          } catch (e) {
            exe_result = [this.executeForm.parameters]
          }
        }
        
        const response = await postRequest('exeChain', {
          chain_id: (executingChain._id.$oid || executingChain._id.$id),
          id: '',
          name: this.executeForm.name,
          user_id: getUserId(),
          exe_result: exe_result
        })
        
        if (response.data && response.data.code === 0) {
          this.$message.success('Chain executed successfully')
          this.executeDialogVisible = false
          this.loadChains()
        } else {
          this.$message.error('Failed to execute chain: ' + (response.data && response.data.msg ? response.data.msg : 'Unknown error'))
        }
      } catch (error) {
        console.error('Error executing chain:', error)
        this.$message.error('Failed to execute chain')
      } finally {
        this.executing = false
      }
    },

    addEffect() {
      const newEffect = { name: '', prop: '', model: '', is_splice: false, output_form: '', enabled: true }
      this.chainForm.effect_list.push(newEffect)
      console.log('Added new effect:', newEffect)
      console.log('Current effect list:', this.chainForm.effect_list)
    },

    removeEffect(index) {
      if (this.chainForm.effect_list.length > 1) {
        this.chainForm.effect_list.splice(index, 1)
      }
    },

    moveEffectUp(index) {
      if (index > 0) {
        const effects = this.chainForm.effect_list
        const temp = effects[index]
        this.$set(effects, index, effects[index - 1])
        this.$set(effects, index - 1, temp)
      }
    },

    moveEffectDown(index) {
      if (index < this.chainForm.effect_list.length - 1) {
        const effects = this.chainForm.effect_list
        const temp = effects[index]
        this.$set(effects, index, effects[index + 1])
        this.$set(effects, index + 1, temp)
      }
    },

    handleDialogClose() {
      this.$refs.chainForm && this.$refs.chainForm.resetFields()
    },

    getEffectCount(chain) {
      if (!chain.effect_list) return 0
      return Array.isArray(chain.effect_list) ? chain.effect_list.length : 0
    },

    getRecordCount(chain) {
      return chain.chain_records ? chain.chain_records.length : 0
    },

    formatTime(timestamp) {
      if (!timestamp) return 'N/A'
      return new Date(timestamp * 1000).toLocaleString()
    },

    viewRecord(record) {
      this.$alert(JSON.stringify(record.exe_result || {}, null, 2), 'Execution Results', {
        confirmButtonText: 'Close'
      })
    },

    getEffectDisplayName(effect) {
      if (typeof effect === 'object' && effect.name) {
        return effect.name
      }
      if (typeof effect === 'string') {
        return effect.length > 30 ? effect.substring(0, 30) + '...' : effect
      }
      return 'Unnamed Effect'
    },

    getStatusType(chain) {
      if (!chain.effect_list || chain.effect_list.length === 0) return 'info'
      if (chain.chain_records && chain.chain_records.length > 0) return 'success'
      return 'warning'
    },

    getStatusText(chain) {
      if (!chain.effect_list || chain.effect_list.length === 0) return 'Empty'
      if (chain.chain_records && chain.chain_records.length > 0) return 'Active'
      return 'Ready'
    },

    getDataObjectName(dataObjectId) {
      if (!dataObjectId || !this.dataObjects) return dataObjectId
      const dataObject = this.dataObjects.find(obj => 
        (obj._id && (obj._id.$oid || obj._id.$id) === dataObjectId) || 
        obj._id === dataObjectId || 
        obj.id === dataObjectId
      )
      return dataObject ? dataObject.name : dataObjectId
    },

    async generateAITitle() {
      if (!this.chainForm.description && (!this.chainForm.effect_list || this.chainForm.effect_list.length === 0)) {
        this.$message.warning('Please provide a description or configure some effects first')
        return
      }

      try {
        this.generatingTitle = true
        
        // Create prompt for AI title generation
        let prompt = 'Generate a concise, descriptive title (max 50 characters) for this AI workflow chain based on:'
        
        if (this.chainForm.description) {
          prompt += `\n\nDescription: ${this.chainForm.description}`
        }
        
        if (this.chainForm.effect_list && this.chainForm.effect_list.length > 0) {
          prompt += '\n\nWorkflow Steps:'
          this.chainForm.effect_list.forEach((effect, index) => {
            if (effect.name || effect.prop) {
              prompt += `\n${index + 1}. ${effect.name || 'Step ' + (index + 1)}: ${effect.prop || 'No description'}`
            }
          })
        }
        
        prompt += '\n\nRespond with just the title, no additional text or formatting.'
        
        const response = await postRequest('exeModelGeneral', {
          content: prompt,
          model: 'gpt-4o-mini'
        })
        
        if (response.data && response.data.code === 0 && response.data.data) {
          const generatedTitle = response.data.data.trim().replace(/^"|"$/g, '').substring(0, 100)
          this.chainForm.name = generatedTitle
          this.$message.success('AI title generated successfully')
        } else {
          this.$message.error('Failed to generate AI title')
        }
      } catch (error) {
        console.error('Error generating AI title:', error)
        this.$message.error('Failed to generate AI title')
      } finally {
        this.generatingTitle = false
      }
    },

    async generateAIEffects() {
      if (!this.chainForm.name && !this.chainForm.description) {
        this.$message.warning('Please provide a workflow name or description first')
        return
      }

      // Check if there's existing content in effect list
      const hasExistingEffects = this.chainForm.effect_list && 
        this.chainForm.effect_list.length > 0 && 
        this.chainForm.effect_list.some(effect => effect.name || effect.prop || effect.model)

      if (hasExistingEffects) {
        try {
          await this.$confirm(
            'This will overwrite your existing effect list. Do you want to proceed?',
            'Confirm Overwrite',
            {
              confirmButtonText: 'Yes, Proceed',
              cancelButtonText: 'Cancel',
              type: 'warning'
            }
          )
        } catch (error) {
          // User cancelled
          return
        }
      }

      try {
        this.generatingEffects = true
        
        // Create prompt for AI effects generation
        let prompt = 'Generate a standardized JSON array of AI workflow effects based on the following workflow details. Each effect should follow this exact structure:\n\n{\n  "name": "Effect Name",\n  "prop": "Detailed prompt/description for the AI model",\n  "model": "gpt-4o",\n  "is_splice": false\n}\n\n'
        
        if (this.chainForm.name) {
          prompt += `Workflow Name: ${this.chainForm.name}\n\n`
        }
        
        if (this.chainForm.description) {
          prompt += `Workflow Description: ${this.chainForm.description}\n\n`
        }
        
        prompt += 'Generate 3-5 logical workflow steps that would accomplish this workflow. Each effect should have:\n'
        prompt += '- A clear, descriptive name\n'
        prompt += '- A detailed prompt that provides context, instructions, and requirements for the AI model\n'
        prompt += '- An appropriate AI model (choose from: gpt-4o, gpt-3.5-turbo, claude-3-5-sonnet-20240620, deepseek-chat)\n'
        prompt += '- is_splice set to false unless the step involves processing large content that needs to be split\n\n'
        prompt += 'IMPORTANT: Respond with ONLY the raw JSON array. Do NOT use markdown code blocks, backticks, or any formatting markers. Just return clean JSON text that can be directly parsed.'
        
        const response = await postRequest('exeModelGeneral', {
          content: prompt,
          model: 'gpt-4o'
        })
        
        if (response.data && response.data.code === 0 && response.data.data) {
          try {
            // Clean the response data by removing potential markdown markers
            let cleanedData = response.data.data.trim()
            // Remove markdown code block markers if present
            cleanedData = cleanedData.replace(/^```json\s*/i, '').replace(/\s*```$/i, '')
            cleanedData = cleanedData.replace(/^```\s*/, '').replace(/\s*```$/i, '')
            
            const generatedEffects = JSON.parse(cleanedData)
            
            if (Array.isArray(generatedEffects) && generatedEffects.length > 0) {
              // Validate each effect has the required structure
              const validatedEffects = generatedEffects.map(effect => ({
                name: effect.name || '',
                prop: effect.prop || '',
                model: effect.model || 'gpt-4o',
                is_splice: effect.is_splice || false,
                output_form: effect.output_form || ''
              }))
              
              this.chainForm.effect_list = validatedEffects
              this.$message.success(`Generated ${validatedEffects.length} AI effects successfully`)
            } else {
              throw new Error('Invalid effects format')
            }
          } catch (parseError) {
            console.error('Error parsing AI-generated effects:', parseError)
            this.$message.error('Failed to parse AI-generated effects')
          }
        } else {
          this.$message.error('Failed to generate AI effects')
        }
      } catch (error) {
        console.error('Error generating AI effects:', error)
        this.$message.error('Failed to generate AI effects')
      } finally {
        this.generatingEffects = false
      }
    },

    async generateAIDescription() {
      if (!this.chainForm.effect_list || this.chainForm.effect_list.length === 0) {
        this.$message.warning('Please configure some effects first')
        return
      }

      try {
        this.generatingDescription = true
        
        // Create prompt for AI description generation
        let prompt = 'Generate a clear, concise description (max 300 characters) for this AI workflow chain based on its configured steps:'
        
        prompt += '\n\nWorkflow Steps:'
        this.chainForm.effect_list.forEach((effect, index) => {
          if (effect.name || effect.prop) {
            const stepName = effect.name || `Step ${index + 1}`
            const stepDesc = effect.prop || 'No description'
            const model = effect.model ? ` (using ${effect.model})` : ''
            prompt += `\n${index + 1}. ${stepName}: ${stepDesc}${model}`
          }
        })
        
        if (this.chainForm.name) {
          prompt += `\n\nWorkflow Name: ${this.chainForm.name}`
        }
        
        prompt += '\n\nRespond with just the description that explains what this workflow accomplishes and its purpose, no additional text or formatting.'
        
        const response = await postRequest('exeModelGeneral', {
          content: prompt,
          model: 'gpt-4o-mini'
        })
        
        if (response.data && response.data.code === 0 && response.data.data) {
          const generatedDescription = response.data.data.trim().replace(/^"|"$/g, '').substring(0, 500)
          this.chainForm.description = generatedDescription
          this.$message.success('AI description generated successfully')
        } else {
          this.$message.error('Failed to generate AI description')
        }
      } catch (error) {
        console.error('Error generating AI description:', error)
        this.$message.error('Failed to generate AI description')
      } finally {
        this.generatingDescription = false
      }
    }
  }
}
</script>

<style scoped>
.aiffect-chain {
  padding: 20px;
}

.chain-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 20px;
}

.chain-header h2 {
  margin: 0;
  color: #303133;
}

.chain-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 24px;
}

.chain-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e4e7ed;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.chain-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  border-color: #409eff;
}

.card-header {
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.chain-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #303133;
  font-size: 16px;
  margin-bottom: 12px;
}

.public-badge {
  margin-left: 8px;
  border-radius: 4px;
  font-weight: normal;
}

.chain-name i {
  color: #409eff;
  font-size: 18px;
}

.chain-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #909399;
  margin-bottom: 16px;
}

.effect-count, .execution-count {
  display: flex;
  align-items: center;
  gap: 4px;
}

.effect-count i, .execution-count i {
  font-size: 14px;
}

.chain-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.action-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

.view-btn:hover {
  background: #409eff !important;
  color: white !important;
}

.edit-btn:hover {
  background: #e6a23c !important;
  color: white !important;
}

.execute-btn:hover {
  background: #67c23a !important;
  color: white !important;
}

.share-btn:hover {
  background: #909399 !important;
  color: white !important;
}

.delete-btn:hover {
  background: #f56c6c !important;
  color: white !important;
}

.card-content {
  padding: 16px 20px 20px 20px;
}

.chain-description {
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
  min-height: 20px;
}

.chain-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chain-collaborators {
  display: flex;
  align-items: center;
}

.collaborator-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f0f2f5;
  border-radius: 12px;
  font-size: 12px;
  color: #606266;
}

.collaborator-indicator i {
  font-size: 14px;
  color: #409eff;
}

.chain-status {
  display: flex;
  justify-content: flex-end;
}

.status-tag {
  font-weight: 500;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
  border: 2px dashed #e4e7ed;
  color: #909399;
}

.empty-icon {
  margin-bottom: 20px;
}

.empty-icon i {
  font-size: 48px;
  color: #c0c4cc;
}

.empty-state h3 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 20px;
  font-weight: 600;
}

.empty-state p {
  margin: 0 0 24px 0;
  color: #909399;
  font-size: 14px;
  line-height: 1.5;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
}

.effect-list-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 15px;
  background-color: #fafafa;
}

/* Chain Edit Dialog Styles */
:deep(.chain-edit-dialog) {
  border-radius: 12px;
}

:deep(.chain-edit-dialog .el-dialog__header) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  padding: 20px 24px;
}

:deep(.chain-edit-dialog .el-dialog__title) {
  color: white;
  font-size: 20px;
  font-weight: 600;
}

:deep(.chain-edit-dialog .el-dialog__headerbtn .el-dialog__close) {
  color: white;
  font-size: 18px;
}

:deep(.chain-edit-dialog .el-dialog__body) {
  padding: 0;
}

:deep(.chain-edit-dialog .el-dialog__footer) {
  padding: 20px 24px;
  border-top: 1px solid #f0f2f5;
  background: #fafbfc;
}

.chain-edit-container {
  padding: 24px;
}

.form-section {
  margin-bottom: 32px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f2f5;
}

.section-header i {
  color: #409eff;
  font-size: 20px;
}

.section-header h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.header-buttons {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.ai-generate-effects-btn {
  color: #67c23a;
  border: 1px solid #67c23a;
  background: transparent;
  transition: all 0.3s ease;
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-generate-effects-btn:hover {
  background: #f0f9ff;
  color: #67c23a;
  border-color: #67c23a;
}

.ai-generate-effects-btn:disabled {
  color: #c0c4cc;
  border-color: #e4e7ed;
  cursor: not-allowed;
}

.ai-generate-effects-btn:disabled:hover {
  background: transparent;
  color: #c0c4cc;
  border-color: #e4e7ed;
}

.chain-name-item {
  margin-bottom: 20px;
}

.name-input-container {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.chain-name-input {
  font-size: 16px;
  flex: 1;
}

.ai-generate-title-btn {
  color: #409eff;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-generate-title-btn:hover {
  background: #ecf5ff;
  color: #409eff;
}

.ai-generate-title-btn:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.ai-generate-title-btn:disabled:hover {
  background: transparent;
}

.chain-description-item {
  margin-bottom: 20px;
}

.description-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.description-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.description-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.description-label i {
  color: #409eff;
  font-size: 16px;
}

.ai-generate-btn {
  color: #409eff;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ai-generate-btn:hover {
  background: #ecf5ff;
  color: #409eff;
}

.chain-description-input :deep(.el-textarea__inner) {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  transition: all 0.3s ease;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
}

.chain-description-input :deep(.el-textarea__inner):focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.chain-description-input :deep(.el-textarea__inner)::placeholder {
  color: #c0c4cc;
  font-style: italic;
}

.system-prompt-item {
  margin-bottom: 20px;
}

.system-prompt-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.system-prompt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.system-prompt-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.system-prompt-label i {
  color: #409eff;
  font-size: 16px;
}

.system-prompt-input :deep(.el-textarea__inner) {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  transition: all 0.3s ease;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
}

.system-prompt-input :deep(.el-textarea__inner):focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.system-prompt-input :deep(.el-textarea__inner)::placeholder {
  color: #c0c4cc;
  font-style: italic;
}

.system-prompt-description {
  font-size: 12px;
  color: #909399;
  margin-top: -4px;
  line-height: 1.4;
}

.store-public-item {
  margin-bottom: 0;
}

.store-public-container {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
}

.store-public-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.store-public-container .el-radio-group {
  flex: 1;
}

.store-public-container .info-icon {
  color: #909399;
  cursor: help;
  font-size: 16px;
  transition: color 0.3s;
}

.store-public-container .info-icon:hover {
  color: #409eff;
}

.chain-name-input :deep(.el-input__prepend) {
  background: #409eff;
  color: white;
  border: none;
  padding: 0 16px;
}

.effects-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.effect-card {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  overflow: hidden;
}

.effect-card:hover {
  border-color: #409eff;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.15);
}

.effect-card.effect-disabled {
  opacity: 0.5;
  background: #f5f7fa;
  border-color: #dcdfe6;
}

.effect-card.effect-disabled:hover {
  border-color: #dcdfe6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.effect-card.effect-disabled .effect-number-badge {
  background: linear-gradient(135deg, #909399 0%, #c0c4cc 100%);
}

.effect-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #f0f2f5;
}

.effect-number-badge {
  background: linear-gradient(135deg, #409eff 0%, #67c23a 100%);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.effect-title {
  flex: 1;
  min-width: 0;
}

.effect-name-input :deep(.el-input__inner) {
  border: none;
  background: transparent;
  font-weight: 500;
  color: #303133;
  font-size: 16px;
}

.effect-name-input :deep(.el-input__inner):focus {
  background: #f5f7fa;
  border: 1px solid #409eff;
  border-radius: 6px;
}

.effect-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.effect-enabled-switch {
  margin-right: 8px;
}

.move-btn, .delete-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.move-btn:hover {
  background: #ecf5ff;
  color: #409eff;
}

.delete-btn:hover {
  background: #fef0f0;
  color: #f56c6c;
}

.effect-card-content {
  padding: 20px;
}

.effect-form-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.model-field {
  flex: 1;
  min-width: 0;
}

.processing-field {
  flex: 1;
  min-width: 0;
}

.effect-form-field {
  display: flex;
  flex-direction: column;
}

.prompt-field {
  margin-bottom: 0;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
  font-weight: 500;
}

.field-label i {
  color: #409eff;
  font-size: 16px;
}

.effect-prompt-input :deep(.el-textarea__inner) {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  transition: all 0.3s ease;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
}

.effect-prompt-input :deep(.el-textarea__inner):focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.effect-prompt-input :deep(.el-textarea__inner)::placeholder {
  color: #c0c4cc;
  font-style: italic;
}

.effect-model-select {
  width: 100%;
}

.effect-model-select :deep(.el-input__inner) {
  border-radius: 8px;
}

.processing-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.splice-switch {
  margin-bottom: 4px;
}

.switch-description {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.output-form-field {
  margin-bottom: 20px;
}

.effect-output-form-select {
  width: 100%;
}

.effect-output-form-select :deep(.el-input__inner) {
  border-radius: 8px;
}

.output-form-description {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  line-height: 1.4;
}

.empty-effects {
  text-align: center;
  padding: 60px 20px;
  background: #fafbfc;
  border: 2px dashed #e4e7ed;
  border-radius: 12px;
}

.empty-effects-icon {
  margin-bottom: 16px;
}

.empty-effects-icon i {
  font-size: 48px;
  color: #c0c4cc;
}

.empty-effects h4 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.empty-effects p {
  margin: 0 0 24px 0;
  color: #909399;
  font-size: 14px;
  line-height: 1.5;
}

.dialog-footer {
  text-align: right;
}

.save-btn {
  padding: 12px 24px;
  font-weight: 500;
}

.save-btn i {
  margin-right: 6px;
}

/* Share Dialog Styles */
:deep(.share-dialog) {
  border-radius: 12px;
}

:deep(.share-dialog .el-dialog__header) {
  background: linear-gradient(135deg, #409eff 0%, #67c23a 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  padding: 20px 24px;
}

:deep(.share-dialog .el-dialog__title) {
  color: white;
  font-size: 20px;
  font-weight: 600;
}

:deep(.share-dialog .el-dialog__headerbtn .el-dialog__close) {
  color: white;
  font-size: 18px;
}

.share-container {
  padding: 24px;
}

.share-header {
  margin-bottom: 24px;
  text-align: center;
}

.share-header h3 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 20px;
  font-weight: 600;
}

.share-description {
  color: #606266;
  font-size: 14px;
  margin: 0;
}

.collaborator-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.add-collaborator {
  display: flex;
  gap: 12px;
}

.current-collaborators h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.collaborator-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
}

.collaborator-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.collaborator-item:hover {
  background: #ecf5ff;
  transform: translateX(4px);
}

.collaborator-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #303133;
  font-size: 14px;
}

.collaborator-info i {
  color: #409eff;
  font-size: 16px;
}

.remove-btn {
  color: #f56c6c;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.remove-btn:hover {
  background: #fef0f0;
}

.no-collaborators {
  text-align: center;
  padding: 40px;
  background: #fafbfc;
  border: 2px dashed #e4e7ed;
  border-radius: 8px;
}

.no-collaborators i {
  font-size: 36px;
  color: #c0c4cc;
  margin-bottom: 12px;
  display: block;
}

.no-collaborators p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}
</style>